import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import { getAccessToken } from '../utils/authSession';
import TextEditor from '../components/TextEditor';

function AdminEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image: '',
    event_date: '',
    location: ''
  });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = getAccessToken();
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchEvents();
  }, [navigate, search, statusFilter, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const token = getAccessToken();
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      params.append('page', String(currentPage));
      params.append('limit', '50');

      const response = await axios.get(`/api/events?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setEvents(response.data.events);
      setPagination(response.data.pagination);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) {
      setImagePreview(null);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      e.target.value = ''; // Reset file input
      setImagePreview(null);
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    setUploadingImage(true);
    try {
      const token = getAccessToken();
      const uploadFormData = new FormData();
      uploadFormData.append('image', file);

      const response = await axios.post('/api/upload', uploadFormData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      // Use filename directly from response (it's the actual filename without path)
      const filename = response.data.filename;
      
      if (!filename) {
        throw new Error('No filename returned from server');
      }
      
      // Update form data with the filename
      setFormData(prev => ({ ...prev, image: filename }));
      
      // Keep the preview showing the selected image (from FileReader)
      // This ensures user always sees the image they selected
      // Don't clear imagePreview - let it show until form is submitted
      
      // Reset file input to allow selecting the same file again if needed
      e.target.value = '';
    } catch (error) {
      alert('Failed to upload image');
      console.error('Error uploading image:', error);
      setImagePreview(null);
      e.target.value = ''; // Reset file input on error
    } finally {
      setUploadingImage(false);
    }
  };

  const handleAdd = () => {
    setFormData({
      title: '',
      description: '',
      image: '',
      event_date: '',
      location: ''
    });
    setFormError('');
    setImagePreview(null);
    setUploadingImage(false);
    setShowAddModal(true);
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    // Format date for datetime-local input
    const eventDate = event.event_date ? new Date(event.event_date).toISOString().slice(0, 16) : '';
    setFormData({
      title: event.title || '',
      description: event.description || '',
      image: event.image || '',
      event_date: eventDate,
      location: event.location || ''
    });
    setFormError('');
    setImagePreview(null);
    setUploadingImage(false);
    setShowEditModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);

    if (!formData.title || !formData.description || !formData.event_date) {
      setFormError('Title, description, and event date are required');
      setFormLoading(false);
      return;
    }

    try {
      const token = getAccessToken();
      const url = editingEvent ? `/api/events/${editingEvent.id}` : '/api/events';
      const method = editingEvent ? 'put' : 'post';

      // Convert datetime-local to ISO string
      const eventDate = new Date(formData.event_date).toISOString();

      await axios[method](url, {
        ...formData,
        event_date: eventDate
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setShowAddModal(false);
      setShowEditModal(false);
      setEditingEvent(null);
      fetchEvents();
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
      setFormError(error.response?.data?.error || 'Failed to save event');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;

    try {
      const token = getAccessToken();
      await axios.delete(`/api/events/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      fetchEvents();
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
      alert('Failed to delete event');
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AdminLayout>
      <div className="container-fluid p-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>Events Management</h2>
          <button className="btn btn-primary" onClick={handleAdd}>
            <i className="bi bi-plus-circle me-2"></i>Add New Event
          </button>
        </div>

        {/* Filters */}
        <div className="row mb-3">
          <div className="col-md-6">
            <input
              type="text"
              className="form-control"
              placeholder="Search events..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="col-md-4">
            <select
              className="form-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Status</option>
              <option value="upcoming">Upcoming</option>
              <option value="past">Past</option>
            </select>
          </div>
        </div>

        {/* Events List */}
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border" role="status"></div>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-hover">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Date & Time</th>
                  <th>Location</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {events.map(event => (
                  <tr key={event.id}>
                    <td>{event.title}</td>
                    <td>{formatDateTime(event.event_date)}</td>
                    <td>{event.location || '-'}</td>
                    <td>
                      <span className={`badge ${event.status === 'upcoming' ? 'bg-success' : 'bg-secondary'}`}>
                        {event.status}
                      </span>
                    </td>
                    <td>
                      <button className="btn btn-sm btn-primary me-2" onClick={() => handleEdit(event)}>
                        <i className="bi bi-pencil"></i>
                      </button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(event.id)}>
                        <i className="bi bi-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
            {pagination.pages > 1 && (
              <div className="mt-3 d-flex justify-content-between align-items-center">
                <p className="text-muted mb-0">
                  Page {pagination.page} of {pagination.pages} (Total: {pagination.total})
                </p>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={loading || (pagination.page || 1) <= 1}
                  >
                    Previous
                  </button>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setCurrentPage((prev) => prev + 1)}
                    disabled={loading || (pagination.page || 1) >= (pagination.pages || 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Add Modal */}
        {showAddModal && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', overflowY: 'auto' }}>
            <div className="modal-dialog modal-lg modal-dialog-scrollable" style={{ maxHeight: '90vh', margin: '2rem auto' }}>
              <div className="modal-content" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
                <div className="modal-header" style={{ flexShrink: 0 }}>
                  <h5 className="modal-title">Add New Event</h5>
                  <button type="button" className="btn-close" onClick={() => {
                    setShowAddModal(false);
                    setImagePreview(null);
                  }}></button>
                </div>
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                  <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>
                    {formError && <div className="alert alert-danger">{formError}</div>}
                    
                    <div className="mb-3">
                      <label className="form-label">Title *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Event Date & Time *</label>
                      <input
                        type="datetime-local"
                        className="form-control"
                        value={formData.event_date}
                        onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Location</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.location}
                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                        placeholder="e.g., Main Auditorium, Conference Hall"
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Event Image/Poster</label>
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*"
                        onChange={handleImageUpload}
                        disabled={uploadingImage}
                      />
                      {uploadingImage && <div className="text-muted mt-2">Uploading...</div>}
                      {/* Show preview of selected file (before or after upload) */}
                      {imagePreview ? (
                        <div className="mt-2">
                          <p className="text-muted small mb-1">
                            {uploadingImage ? 'Uploading...' : 'Selected image:'}
                          </p>
                          <img 
                            src={imagePreview} 
                            alt="Preview" 
                            style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'cover', borderRadius: '4px' }}
                          />
                          {formData.image && !uploadingImage && (
                            <button
                              type="button"
                              className="btn btn-sm btn-danger ms-2 mt-2"
                              onClick={() => {
                                setFormData({ ...formData, image: '' });
                                setImagePreview(null);
                              }}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      ) : formData.image ? (
                        <div className="mt-2">
                          <p className="text-muted small mb-1">Current image:</p>
                          <img 
                            src={formData.image.startsWith('/uploads/') ? formData.image : `/uploads/${formData.image}`} 
                            alt="Preview" 
                            style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'cover', borderRadius: '4px' }}
                            onError={(e) => {
                              console.error('Error loading image:', formData.image);
                              e.target.src = '/img/Gallery/opd1.jpg';
                            }}
                          />
                          <button
                            type="button"
                            className="btn btn-sm btn-danger ms-2 mt-2"
                            onClick={() => {
                              setFormData({ ...formData, image: '' });
                              setImagePreview(null);
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      ) : null}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Description *</label>
                      <TextEditor
                        value={formData.description}
                        onChange={(description) => setFormData({ ...formData, description })}
                      />
                    </div>
                  </div>
                  <div className="modal-footer" style={{ flexShrink: 0 }}>
                    <button type="button" className="btn btn-secondary" onClick={() => {
                      setShowAddModal(false);
                      setImagePreview(null);
                    }}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={formLoading}>
                      {formLoading ? 'Saving...' : 'Save Event'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', overflowY: 'auto' }}>
            <div className="modal-dialog modal-lg modal-dialog-scrollable" style={{ maxHeight: '90vh', margin: '2rem auto' }}>
              <div className="modal-content" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
                <div className="modal-header" style={{ flexShrink: 0 }}>
                  <h5 className="modal-title">Edit Event</h5>
                  <button type="button" className="btn-close" onClick={() => {
                    setShowEditModal(false);
                    setImagePreview(null);
                  }}></button>
                </div>
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                  <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>
                    {formError && <div className="alert alert-danger">{formError}</div>}
                    
                    <div className="mb-3">
                      <label className="form-label">Title *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Event Date & Time *</label>
                      <input
                        type="datetime-local"
                        className="form-control"
                        value={formData.event_date}
                        onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Location</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.location}
                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Event Image/Poster</label>
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*"
                        onChange={handleImageUpload}
                        disabled={uploadingImage}
                      />
                      {uploadingImage && <div className="text-muted mt-2">Uploading...</div>}
                      {/* Show preview of selected file before upload, or uploaded image */}
                      {imagePreview ? (
                        <div className="mt-2">
                          <p className="text-muted small mb-1">Selected image preview:</p>
                          <img 
                            src={imagePreview} 
                            alt="Preview" 
                            style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'cover', borderRadius: '4px' }}
                          />
                        </div>
                      ) : formData.image ? (
                        <div className="mt-2">
                          <p className="text-muted small mb-1">Current image:</p>
                          <img 
                            src={formData.image.startsWith('/uploads/') ? formData.image : `/uploads/${formData.image}`} 
                            alt="Preview" 
                            style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'cover', borderRadius: '4px' }}
                            onError={(e) => {
                              e.target.src = '/img/Gallery/opd1.jpg';
                            }}
                          />
                          <button
                            type="button"
                            className="btn btn-sm btn-danger ms-2 mt-2"
                            onClick={() => {
                              setFormData({ ...formData, image: '' });
                              setImagePreview(null);
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      ) : null}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Description *</label>
                      <TextEditor
                        value={formData.description || ''}
                        onChange={(e) => {
                          const value = typeof e === 'string' ? e : (e?.target?.value || '');
                          setFormData({ ...formData, description: value });
                        }}
                      />
                    </div>
                  </div>
                  <div className="modal-footer" style={{ flexShrink: 0 }}>
                    <button type="button" className="btn btn-secondary" onClick={() => {
                      setShowEditModal(false);
                      setImagePreview(null);
                    }}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={formLoading}>
                      {formLoading ? 'Saving...' : 'Update Event'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminEvents;
